import { useState } from "react";
import { Clock, ChevronUp, Zap } from "lucide-react";
import { useSpoilerOffset } from "@/hooks/useSpoilerOffset";
import { useLanguage } from "@/hooks/useLanguage";

/**
 * TV Sync UX (Phase 2 rebuild) — collapsed pill by default,
 * expands on tap. Adaptive labels: Sincronizar TV → Calibrando →
 * TV Sync +Xs → Ultra Live. No "−60s" forced negative display.
 */
export function SpoilerSyncSlider({ compact = false }: { compact?: boolean }) {
  const { t } = useLanguage();
  const { offsetSec, setOffset } = useSpoilerOffset();
  const [expanded, setExpanded] = useState(false);
  const active = offsetSec > 0;
  const ultra = offsetSec === 0;

  const pillLabel = !active
    ? t("tv_sync_setup")
    : ultra
      ? t("tv_sync_ultra")
      : `TV Sync +${offsetSec}s`;

  if (!expanded) {
    // Compact icon-only chip — opens the quick-adjust panel on tap.
    // Avoids visual duplication with the global Ultra Live status pill.
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        title={pillLabel}
        aria-label={`${t("tv_sync_expand")} — ${pillLabel}`}
        className={`inline-flex h-7 w-7 items-center justify-center rounded-full border transition ${
          active
            ? "border-primary/60 bg-primary/10 text-primary"
            : "border-border/70 bg-background/60 text-muted-foreground hover:border-primary/40"
        }`}
      >
        {active ? <Clock className="h-3.5 w-3.5" /> : <Zap className="h-3.5 w-3.5" />}
      </button>
    );
  }

  return (
    <div className={`rounded-xl border ${active ? "border-primary/60 bg-primary/10" : "border-border bg-background/60"} px-3 py-2`}>
      <div className="flex items-center justify-between gap-3">
        <label htmlFor="spoiler-sync" className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider">
          <Clock className="h-3.5 w-3.5" />
          {t("spoiler_sync_title")}
        </label>
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs font-black tabular-nums">{pillLabel}</span>
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="rounded-full p-1 text-muted-foreground hover:text-foreground"
            aria-label={t("tv_sync_collapse")}
          >
            <ChevronUp className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      {!compact && <p className="mt-1 text-[10px] text-muted-foreground">{t("spoiler_sync_desc")}</p>}
      <input id="spoiler-sync" type="range" min={0} max={60} step={1} value={offsetSec}
        onChange={(e) => setOffset(Number(e.target.value))}
        className="mt-2 w-full accent-primary" aria-label={t("tv_sync_offset_label")} />
    </div>
  );
}